import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Search, FileCheck, GitBranch, RefreshCcw, Shield,
  ArrowRight, Mail, Globe, Linkedin, MapPin,
  CheckCircle2, AlertTriangle, XCircle, Clock, AlertCircle, FileText, Users, ExternalLink,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MATTER_TYPES = [
  'Family law / divorce',
  'Probate / estate',
  'Bankruptcy',
  'Insurance claim',
  'Litigation / discovery',
  'Other',
];

const STATUS_LABELS = [
  { label: 'Verified (Record-Supported)', icon: CheckCircle2, cls: 'status-verified' },
  { label: 'Partially Supported', icon: AlertTriangle, cls: 'status-partial' },
  { label: 'Unverified (Insufficient Record Support)', icon: XCircle, cls: 'status-unverified' },
  { label: 'Conflict Flag', icon: AlertCircle, cls: 'status-conflict' },
  { label: 'Open Item (Requires Clarification / Compulsion)', icon: Clock, cls: 'status-open' },
  { label: 'Out of Scope', icon: Clock, cls: 'status-open' },
];

const PROCESS_STEPS = [
  { num: '01', title: 'Discuss a Matter', desc: 'Contact VARfacts to discuss the matter, the record set involved, and the scope of the evaluation.' },
  { num: '02', title: 'Record Preparation', desc: 'Produced records are transferred through a secure Sync portal, cataloged, and evaluated under the VAR Framework.' },
  { num: '03', title: 'Receive VARfacts Report', desc: 'A structured, source-cited VARfacts Report is delivered to counsel, organized for review, discovery, and disclosure.' },
];

const PUBLICATIONS = [
  { title: 'Evaluating Digital Asset Records: Evidentiary Sufficiency in Fragmented Data Sets', subtitle: 'SSRN Paper', url: 'http://ssrn.com/abstract=6594598', linkLabel: 'View on SSRN' },
  { title: 'VAR Verification Status Standard v1.2 (Public)', subtitle: 'Effective February 26, 2026', url: '/documents/VAR_Verification_Status_Standard_v1_2_Public.pdf', linkLabel: 'View Standard' },
  { title: 'VAR Framework v1.0 \u2014 Public Exposure Draft', subtitle: 'Published May 1, 2026', url: '/virtual-asset-registry-framework', linkLabel: 'View Framework' },
  { title: 'SEC Crypto Task Force \u2014 Written Submission', subtitle: 'Filed and published', url: 'https://www.sec.gov/featured-topics/crypto-task-force/crypto-task-force-written-input', linkLabel: 'View Submission' },
];

const FAQ_ITEMS = [
  { q: 'What is a VARfacts Report?', a: 'A structured, source-cited evaluation of the produced digital asset record set, organized for counsel review, discovery, and disclosure. Each finding is tied to a specific, indexed source artifact and classified under the Verification Status Standard.' },
  { q: 'What is the Virtual Asset Registry (VAR) Framework?', a: 'A neutral, records-based evaluation framework for digital asset documentation. It is built on three core functions: Attribution Review, Provenance Mapping, and Record Reconciliation. All outputs are source-cited, reproducible, and limited to what the produced record set supports.' },
  { q: 'What is the Verification Status Standard?', a: 'A six-label classification system applied to each evaluated claim or assertion, based solely on the level of record support found in the produced set. Labels range from Verified (Record-Supported) to Out of Scope.' },
  { q: 'Does VARfacts provide legal or investment advice?', a: 'No. VARfacts provides record-based evaluation and verification-status reporting only. VARfacts does not provide legal, tax, or investment advice, and does not provide custody services.' },
  { q: 'How are records transferred?', a: 'All record submissions are transferred exclusively through a secure Sync portal tied to the specific matter. No records are accepted by ordinary email.' },
  { q: 'Who uses VARfacts?', a: 'Family law attorneys, estate and probate counsel, fiduciaries, trustees, executors, and related professionals who need a structured evaluation of produced digital asset record sets.' },
  { q: 'What should I send first?', a: 'A short matter description is enough to begin. If appropriate, VARfacts will request the produced record set or provide a secure upload path for exchange exports, wallet records, screenshots, transaction histories, and related materials.' },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    { "@type": "Organization", "name": "VARfacts", "url": "https://varfacts.com", "description": "Record-based evaluation and verification-status reporting for digital asset record sets." },
    { "@type": "WebSite", "name": "VARfacts", "url": "https://varfacts.com" },
    { "@type": "Service", "name": "VARfacts Report", "provider": { "@type": "Organization", "name": "VARfacts" }, "description": "Structured, source-cited evaluation of produced digital asset record sets under the Virtual Asset Registry (VAR) Framework." },
    { "@type": "FAQPage", "mainEntity": FAQ_ITEMS.map(item => ({ "@type": "Question", "name": item.q, "acceptedAnswer": { "@type": "Answer", "text": item.a } })) },
  ]
};

export default function LandingPage() {
  const [intakeSubmitted, setIntakeSubmitted] = useState(false);
  const [intakeSubmitting, setIntakeSubmitting] = useState(false);
  const [intakeForm, setIntakeForm] = useState({
    name: '', organization: '', email: '', matter_type: '', description: '',
  });

  const handleIntakeChange = (field, value) => {
    setIntakeForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleIntakeSubmit = async (e) => {
    e.preventDefault();
    setIntakeSubmitting(true);
    try {
      await axios.post(`${API}/contact`, intakeForm);
      setIntakeSubmitted(true);
      document.getElementById('intake')?.scrollIntoView({ behavior: 'smooth' });
    } catch {
      toast.error('Failed to send. Please email secure.intake@varfacts.com directly.');
    } finally {
      setIntakeSubmitting(false);
    }
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(JSON_LD);
    document.head.appendChild(script);
    return () => script.remove();
  }, []);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#D4D4D0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <img src="/10_VARfacts_logo_header_400x100.png" alt="VARfacts" className="h-8" data-testid="nav-logo" />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/virtual-asset-registry-framework" className="nav-link text-sm" data-testid="nav-framework">Framework</Link>
            <Link to="/family-law-digital-assets" className="nav-link text-sm" data-testid="nav-family-law">Family Law</Link>
            <Link to="/estate-digital-asset-records" className="nav-link text-sm" data-testid="nav-estates">Estates</Link>
            <Link to="/contact" className="nav-link text-sm" data-testid="nav-contact">Contact</Link>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild className="btn-gold px-5 py-2 rounded-sm text-xs" data-testid="nav-cta">
              <a href="#intake">Contact Us</a>
            </Button>
          </div>
        </div>
      </nav>

      {/* 1. What VARfacts Does (Hero) */}
      <section className="hero-section grain-overlay flex items-center pt-16" data-testid="hero-section">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8">
              <div className="space-y-2 opacity-0 animate-fade-in-up">
                <p className="text-[#C5A55A]/80 text-sm tracking-wider uppercase">
                  Digital Asset Record Review for Legal Matters
                </p>
              </div>
              <p className="text-base text-white/70 max-w-xl leading-relaxed opacity-0 animate-fade-in-up delay-100" data-testid="hero-clarity">
                VARfacts evaluates fragmented digital asset record sets and produces a structured, source-cited VARfacts Report for counsel review.
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1] tracking-tight opacity-0 animate-fade-in-up delay-100" data-testid="hero-h1">
                Turn a disorganized digital asset record set into a structured, source-cited VARfacts Report.
              </h1>
              <p className="text-sm text-white/50 max-w-xl leading-relaxed opacity-0 animate-fade-in-up delay-200" data-testid="hero-body">
                VARfacts evaluates produced digital asset record sets and delivers a structured, source-cited VARfacts Report. The Virtual Asset Registry (VAR) Framework is applied to determine what is supported by the record set, what is missing, and where inconsistencies appear. Every finding is record-bound and limited strictly to what the produced record set supports.
              </p>
              <p className="text-sm text-white/50 max-w-xl leading-relaxed opacity-0 animate-fade-in-up delay-250" data-testid="hero-body-2">
                VARfacts is a neutral reporting service. We do not determine ownership, intent, or ultimate truth. We evaluate what the records support and document the remainder with precision.
              </p>
              <p className="text-xs text-[#C5A55A]/80 italic opacity-0 animate-fade-in-up delay-300" data-testid="hero-disclaimer">
                Record-based evaluation and verification-status reporting only. Not legal, tax, or investment advice. No custody.
              </p>
              <div className="flex flex-wrap gap-4 opacity-0 animate-fade-in-up delay-400">
                <Button asChild className="btn-gold px-6 py-3 h-12 rounded-sm" data-testid="hero-cta-btn">
                  <a href="#intake">
                    Contact Us <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5 hidden lg:block">
              <div className="glass-card rounded-sm p-8 space-y-5 animate-fade-in-up delay-500">
                <p className="text-white/40 text-xs font-semibold uppercase tracking-widest">Verification Status Standard</p>
                <div className="space-y-3">
                  {STATUS_LABELS.map((s) => (
                    <div key={s.label} className="flex items-center gap-3">
                      <s.icon className="w-4 h-4 text-[#C5A55A] flex-shrink-0" />
                      <span className="text-white/80 text-sm">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When to Contact VARfacts */}
      <section className="py-14 md:py-20 bg-white" data-testid="when-to-contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl space-y-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A55A]">Who Should Reach Out</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0B1F3F]">When to Contact VARfacts</h2>
            <div className="w-16 h-0.5 bg-[#C5A55A]" />
            <ul className="space-y-4 text-[#64748B] leading-relaxed">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0B1F3F] mt-2 flex-shrink-0" />
                <span>You have produced digital asset records that do not reconcile across sources</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0B1F3F] mt-2 flex-shrink-0" />
                <span>You are preparing for discovery, disclosure, or settlement and need clarity on what is supported</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0B1F3F] mt-2 flex-shrink-0" />
                <span>You are working with exchange exports, wallet histories, or screenshots that do not form a consistent record</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0B1F3F] mt-2 flex-shrink-0" />
                <span>You need a structured, source-cited view of what the record set supports before relying on it</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 2. What Counsel Receives (Before / After) */}
      <section className="py-14 md:py-20 bg-white" data-testid="before-after-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A55A]">What Counsel Receives</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0B1F3F]">
              From Fragmented Records to a Structured Report
            </h2>
            <div className="w-16 h-0.5 bg-[#C5A55A] mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#F5F3EF] border border-[#D4D4D0] rounded-sm p-8 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#94A3B8]">Before</p>
              <h3 className="font-heading text-xl text-[#0B1F3F] font-semibold">The Produced Record Set</h3>
              <div className="space-y-3 text-sm text-[#64748B] leading-relaxed">
                <p>Chaotic exports from multiple exchanges and wallets</p>
                <p>Partial statements with inconsistent date ranges</p>
                <p>Screenshots of account interfaces with no source verification</p>
                <p>Unverified claims of holdings, transfers, or ownership</p>
                <p>No indexing, no cross-referencing, no structured organization</p>
              </div>
            </div>
            <div className="bg-white border-2 border-[#C5A55A]/30 rounded-sm p-8 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A55A]">After</p>
              <h3 className="font-heading text-xl text-[#0B1F3F] font-semibold">The VARfacts Report</h3>
              <div className="space-y-3 text-sm text-[#64748B] leading-relaxed">
                <p>Structured, source-cited evaluation of the entire produced record set</p>
                <p>Each claim classified under the Verification Status Standard</p>
                <p>Conflicts and discrepancies documented with source references</p>
                <p>Timeline of recorded activity with identified gaps</p>
                <p>Organized for counsel review, discovery, and disclosure</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. How It Works */}
      <section className="py-14 md:py-20 bg-[#F5F3EF]" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-16 space-y-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A55A]">How It Works</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0B1F3F]">The Engagement Process</h2>
            <div className="w-16 h-0.5 bg-[#C5A55A]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {PROCESS_STEPS.map((step) => (
              <div key={step.num} className="space-y-4 group">
                <span className="font-heading text-5xl font-bold text-[#0B1F3F]/10 group-hover:text-[#C5A55A]/30 transition-colors duration-300">{step.num}</span>
                <h3 className="font-heading text-xl text-[#0B1F3F] font-semibold">{step.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Who It Serves */}
      <section className="py-14 md:py-20 bg-white" data-testid="audience-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-14 max-w-2xl space-y-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A55A]">Who It Serves</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0B1F3F]">Serving Counsel, Fiduciaries, Insurers, and Related Professionals</h2>
            <div className="w-16 h-0.5 bg-[#C5A55A]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: 'Family Law', desc: 'Divorce and family law counsel managing digital asset discovery and disclosure.', link: '/family-law-digital-assets', cta: 'Learn More' },
              { icon: FileText, title: 'Estates & Probate', desc: 'Executors, trustees, and estate counsel responsible for accounting of digital asset holdings.', link: '/estate-digital-asset-records', cta: 'Learn More' },
              { icon: Users, title: 'Fiduciaries & Trustees', desc: 'Fiduciaries, guardians, and compliance professionals evaluating digital asset record sets.', link: '/contact', cta: 'Request Evaluation' },
            ].map((a) => (
              <div key={a.title} className="bg-[#F5F3EF] border border-[#D4D4D0] rounded-sm p-6 space-y-4 hover:shadow-md transition-shadow duration-300">
                <div className="w-10 h-10 bg-[#0B1F3F] rounded-sm flex items-center justify-center">
                  <a.icon className="w-5 h-5 text-[#C5A55A]" />
                </div>
                <h3 className="font-heading text-lg text-[#0B1F3F] font-semibold">{a.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{a.desc}</p>
                {a.link && (
                  <Link to={a.link} className="text-xs text-[#C5A55A] font-semibold uppercase tracking-wide hover:text-[#0B1F3F] flex items-center gap-1">
                    {a.cta} <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. VAR Framework */}
      <section id="framework" className="py-14 md:py-20 bg-[#F5F3EF]" data-testid="framework-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5 space-y-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A55A]">The Framework</p>
              <h2 className="font-heading text-3xl sm:text-4xl text-[#0B1F3F] leading-tight">
                Virtual Asset Registry (VAR)
              </h2>
              <div className="w-16 h-0.5 bg-[#C5A55A]" />
              <p className="text-[#64748B] leading-relaxed">
                The Virtual Asset Registry is a records-based evaluation framework built on
                three core functions. All outputs are source-cited, reproducible, and limited
                to what the produced record set supports. VARfacts does not determine ownership,
                intent, or ultimate truth.
              </p>
              <p className="text-xs text-[#94A3B8] italic">
                Record-based evaluation and verification-status reporting only. Not legal, tax, or investment advice. No custody.
              </p>
              <Button asChild variant="ghost" className="text-[#C5A55A] text-xs uppercase tracking-wide font-semibold hover:bg-[#C5A55A]/5 rounded-sm px-5 py-2.5">
                <Link to="/virtual-asset-registry-framework">
                  Learn More About the Framework <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-4 hover:shadow-md transition-shadow duration-300">
                  <div className="w-10 h-10 bg-[#0B1F3F] rounded-sm flex items-center justify-center">
                    <Search className="w-5 h-5 text-[#C5A55A]" />
                  </div>
                  <h3 className="font-heading text-lg text-[#0B1F3F] font-semibold">Attribution Review</h3>
                  <p className="text-sm text-[#64748B] leading-relaxed">
                    Assessing whether produced materials support the asserted relationship between the party and specific accounts or wallets.
                  </p>
                </div>
                <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-4 hover:shadow-md transition-shadow duration-300">
                  <div className="w-10 h-10 bg-[#0B1F3F] rounded-sm flex items-center justify-center">
                    <GitBranch className="w-5 h-5 text-[#C5A55A]" />
                  </div>
                  <h3 className="font-heading text-lg text-[#0B1F3F] font-semibold">Provenance Mapping</h3>
                  <p className="text-sm text-[#64748B] leading-relaxed">
                    Building a documented timeline of activity and identifying discontinuities or missing production.
                  </p>
                </div>
                <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-4 hover:shadow-md transition-shadow duration-300">
                  <div className="w-10 h-10 bg-[#0B1F3F] rounded-sm flex items-center justify-center">
                    <RefreshCcw className="w-5 h-5 text-[#C5A55A]" />
                  </div>
                  <h3 className="font-heading text-lg text-[#0B1F3F] font-semibold">Record Reconciliation</h3>
                  <p className="text-sm text-[#64748B] leading-relaxed">
                    Cross-checking internal consistency across sources and documenting conflicts.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Report */}
      <section className="py-14 md:py-20 bg-white" data-testid="sample-report-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl space-y-6">
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0B1F3F]">See What a VARfacts Report Looks Like</h2>
            <div className="w-16 h-0.5 bg-[#C5A55A]" />
            <p className="text-[#64748B] leading-relaxed">
              Every VARfacts engagement produces a single structured, source-cited report showing what the record set supports, what is missing, and where conflicts exist. Download a sample report to see the format, the verification status labels, and the source citation index in practice.
            </p>
            <a
              href="/api/documents/VARfacts_Sample_Report_VAR-2026-0412.pdf"
              data-testid="sample-report-btn"
              className="inline-flex items-center px-5 py-2.5 text-sm font-semibold border border-[#D4D4D0] rounded-sm text-[#0B1F3F] hover:bg-[#F5F3EF] transition-colors"
            >
              Download Sample Report
            </a>
          </div>
        </div>
      </section>

      {/* 6. Published Work & Authority */}
      <section className="py-14 md:py-20 bg-white" data-testid="published-work-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-14 max-w-2xl space-y-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A55A]">Authority</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0B1F3F]">Published Work &amp; Framework Authority</h2>
            <div className="w-16 h-0.5 bg-[#C5A55A]" />
            <p className="text-[#64748B] leading-relaxed">
              The VAR Framework and its governing standards are publicly available for review by counsel, fiduciaries, and institutional partners.
            </p>
          </div>
          <div className="space-y-4">
            {PUBLICATIONS.map((pub) => (
              <div key={pub.title} className="bg-[#F5F3EF] border border-[#D4D4D0] rounded-sm p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="space-y-1 flex-1">
                  <p className="text-[#0B1F3F] font-semibold">{pub.title}</p>
                  <p className="text-sm text-[#94A3B8]">{pub.subtitle}</p>
                </div>
                <a href={pub.url} target="_blank" rel="noopener noreferrer" className="text-[#C5A55A] hover:text-[#0B1F3F] flex items-center gap-1 text-sm font-semibold" data-testid={`pub-link-${pub.title.slice(0, 10).toLowerCase().replace(/\s/g, '-')}`}>
                  {pub.linkLabel} <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. FAQ */}
      <section className="py-14 md:py-20 bg-[#F5F3EF]" data-testid="faq-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-14 max-w-2xl space-y-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A55A]">FAQ</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0B1F3F]">Frequently Asked Questions</h2>
            <div className="w-16 h-0.5 bg-[#C5A55A]" />
          </div>
          <div className="space-y-4 max-w-3xl">
            {FAQ_ITEMS.map((item) => (
              <div key={item.q} className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-3">
                <p className="text-[#0B1F3F] font-semibold">{item.q}</p>
                <p className="text-sm text-[#64748B] leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Start a Conversation (Intake Form) */}
      <section id="intake" className="py-14 md:py-20 bg-white" data-testid="intake-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mx-auto">
            {intakeSubmitted ? (
              <div className="text-center space-y-6" data-testid="intake-success">
                <div className="w-16 h-16 mx-auto bg-[#0B1F3F] rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-[#C5A55A]" />
                </div>
                <h2 className="font-heading text-3xl text-[#0B1F3F]">Submission Received</h2>
                <p className="text-[#64748B] leading-relaxed">
                  VARfacts responds within one business day. All inquiries are confidential.
                </p>
                <p className="text-sm text-[#94A3B8]">Contact: secure.intake@varfacts.com</p>
              </div>
            ) : (
              <div className="space-y-8">
                <div className="space-y-4">
                  <h2 className="font-heading text-3xl sm:text-4xl text-[#0B1F3F]">Submit a Matter for Evaluation</h2>
                  <div className="w-16 h-0.5 bg-[#C5A55A]" />
                  <p className="text-[#64748B] leading-relaxed">
                    No keys, credentials, or custody required. Just the produced record set.
                  </p>
                </div>
                <form onSubmit={handleIntakeSubmit} className="space-y-5" data-testid="intake-form">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Name *</Label>
                    <Input
                      data-testid="intake-name-input"
                      value={intakeForm.name}
                      onChange={(e) => handleIntakeChange('name', e.target.value)}
                      className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Firm or Organization *</Label>
                    <Input
                      data-testid="intake-org-input"
                      value={intakeForm.organization}
                      onChange={(e) => handleIntakeChange('organization', e.target.value)}
                      className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Email *</Label>
                    <Input
                      data-testid="intake-email-input"
                      type="email"
                      value={intakeForm.email}
                      onChange={(e) => handleIntakeChange('email', e.target.value)}
                      className="h-12 bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Matter Type *</Label>
                    <select
                      data-testid="intake-matter-type"
                      value={intakeForm.matter_type}
                      onChange={(e) => handleIntakeChange('matter_type', e.target.value)}
                      className="w-full h-12 px-3 bg-white border border-[#D4D4D0] rounded-md text-sm text-[#0B1F3F] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F] focus:outline-none"
                      required
                    >
                      <option value="" disabled>Select matter type</option>
                      {MATTER_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Brief description of the record situation</Label>
                    <Textarea
                      data-testid="intake-description-input"
                      value={intakeForm.description}
                      onChange={(e) => handleIntakeChange('description', e.target.value)}
                      placeholder="Optional — describe the record set and what you need evaluated."
                      className="min-h-[120px] bg-white border-[#D4D4D0] focus:border-[#0B1F3F] focus:ring-1 focus:ring-[#0B1F3F]"
                    />
                  </div>
                  <Button
                    data-testid="intake-submit-btn"
                    type="submit"
                    disabled={intakeSubmitting}
                    className="btn-primary-varfacts px-8 py-3 rounded-sm h-12"
                  >
                    {intakeSubmitting ? 'Submitting...' : 'Request an Intake Outline'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                  <p className="text-xs text-[#94A3B8] leading-relaxed">
                    VARfacts responds within one business day. All inquiries are confidential.<br />
                    Contact: <a href="mailto:secure.intake@varfacts.com" className="hover:text-[#0B1F3F]">secure.intake@varfacts.com</a>
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0B1F3F] border-t border-white/10 py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5 space-y-4">
              <img src="/01_VARfacts_logo_white_transparent.png" alt="VARfacts" className="h-8 opacity-90" />
            </div>
            <div className="md:col-span-3 space-y-3">
              <p className="text-white/40 text-xs font-semibold uppercase tracking-widest">Links</p>
              <div className="space-y-2">
                <Link to="/" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Home</Link>
                <Link to="/virtual-asset-registry-framework" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">VAR Framework</Link>
                <Link to="/family-law-digital-assets" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Family Law</Link>
                <Link to="/estate-digital-asset-records" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Estates</Link>
                <Link to="/contact" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Contact</Link>
                <a href="/documents/VAR_Verification_Status_Standard_v1_2_Public.pdf" target="_blank" rel="noopener noreferrer" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">Verification Status Standard v1.2 (Public)</a>
              </div>
            </div>
            <div className="md:col-span-4 space-y-3">
              <p className="text-white/40 text-xs font-semibold uppercase tracking-widest">Contact</p>
              <div className="space-y-2">
                <p className="text-white/60 text-sm">secure.intake@varfacts.com</p>
                <p className="text-white/60 text-sm">Nashville, Tennessee</p>
                <a href="https://www.linkedin.com/company/varfacts" target="_blank" rel="noopener noreferrer" className="block text-white/60 text-sm hover:text-[#C5A55A] transition-colors">
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
          <Separator className="my-8 bg-white/10" />
          <p className="text-white/20 text-xs">&copy; 2026 VARfacts. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}
